// 密な RGBA 配列と PixelSurface の相互変換。
// Canvas に依存しない純粋な配列操作だけを置く (Node テスト可能)。

import type { Rect } from '@/types/geometry'
import { PixelSurface } from './pixelSurface'
import { TILE_SIZE, tileKey, createTile, type Tile } from './tile'

/**
 * 密な RGBA 配列 (w*h*4) から新しい表面を作る。原点は (0,0)。
 * writeRect より速い (タイルへ直接分割する)。
 */
export function surfaceFromRgba(id: string, width: number, height: number, rgba: Uint8ClampedArray): PixelSurface {
  if (rgba.length !== width * height * 4) throw new Error('RGBA 配列長が不正')
  const s = new PixelSurface(id, 4)
  const patches: [string, Tile | null][] = []
  const tilesX = Math.ceil(width / TILE_SIZE)
  const tilesY = Math.ceil(height / TILE_SIZE)
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      const tile = createTile(4)
      const x0 = tx * TILE_SIZE
      const y0 = ty * TILE_SIZE
      const w = Math.min(TILE_SIZE, width - x0)
      const h = Math.min(TILE_SIZE, height - y0)
      for (let y = 0; y < h; y++) {
        const src = ((y0 + y) * width + x0) * 4
        tile.data.set(rgba.subarray(src, src + w * 4), y * TILE_SIZE * 4)
      }
      patches.push([tileKey(tx, ty), tile])
    }
  }
  s.writeTiles(patches)
  return s
}

/** 表面の矩形領域を RGBA 配列として取り出す (ImageData の data に相当) */
export function rgbaFromSurface(surface: PixelSurface, rect: Rect): Uint8ClampedArray {
  if (surface.channels !== 4) throw new Error('RGBA 表面ではない')
  return surface.readRect(rect)
}

/** 単一色で塗りつぶした RGBA 配列 */
export function solidRgba(width: number, height: number, r: number, g: number, b: number, a: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(width * height * 4)
  for (let i = 0; i < out.length; i += 4) {
    out[i] = r
    out[i + 1] = g
    out[i + 2] = b
    out[i + 3] = a
  }
  return out
}
